/**
 * 마감 임박 강조 판정 — 텍스트(D-n)·색·아이콘 3요소의 근거를 한 곳에서 산출한다(대시보드·캘린더 공용).
 *
 * 색 단독으로 상태를 전달하지 않기 위해(WCAG 1.4.1) 배지는 항상 숫자와 상태어를 함께 노출한다.
 * 노출 대상(서버가 뽑는 7일 이내)과는 별개 축 — 이미 뽑힌 항목을 얼마나 강조할지만 정한다.
 *
 * 남은 일수는 **KST 달력일** 기준 — 브라우저 로컬 타임존으로 계산하면 서버 판정과 하루 어긋난다.
 */
import type { Dayjs } from 'dayjs';

import { KST, kstNow, toKstDayjs } from './datetime';

/** 강조 시작 구간(일) — D-7 이내부터 배지를 노출한다. */
export const CLOSING_SOON_DAYS = 7;

/** 최우선 강조 구간(일) — D-3 이내는 "마감 임박"으로 승격. */
export const CLOSING_CRITICAL_DAYS = 3;

export type Urgency = 'critical' | 'normal' | 'passed' | 'soon';

/**
 * 오늘(KST 자정)로부터 마감일까지 남은 달력일 수. 값이 없거나 파싱 불가면 null.
 * `endDate` 는 ISO 날짜 또는 오프셋 포함 ISO-8601 — 둘 다 KST 달력일로 환산한 뒤 차를 낸다.
 */
export function remainingDaysFromToday(
  endDate: string | null | undefined,
  now: Dayjs = kstNow(),
): number | null {
  if (!endDate) return null;
  const end = toKstDayjs(endDate);
  if (!end.isValid()) return null;
  return end.startOf('day').diff(now.tz(KST).startOf('day'), 'day');
}

/** 남은 일수 → 강조 등급. null(마감일 없음)은 강조하지 않는다. */
export function urgencyOf(days: number | null): Urgency {
  if (days === null) return 'normal';
  if (days < 0) return 'passed';
  if (days <= CLOSING_CRITICAL_DAYS) return 'critical';
  if (days <= CLOSING_SOON_DAYS) return 'soon';
  return 'normal';
}

/** 등급 → 배지 문구. D-0 은 "D-0 마감 임박"보다 "오늘 마감"이 즉시 이해된다. 색은 보조, 의미는 단어가 전달한다. */
export function urgencyText(level: Exclude<Urgency, 'normal'>, remaining: number | null): string {
  switch (level) {
    case 'critical':
      return remaining === 0 ? '오늘 마감' : `D-${remaining} 마감 임박`;
    case 'passed':
      return '마감됨';
    case 'soon':
      return `D-${remaining} 마감 예정`;
  }
}
